import React, { useEffect, useState } from "react";

import {
	Button,
	DatePicker,
	InfoMessage,
	Input,
	Modal,
} from "@amphore-dev/amphore-lib";
import i18n, { t } from "i18next";

import { useQuery } from "@tanstack/react-query";

import InvoicesService from "@/services/InvoicesService";

import { invoiceKeys } from "@/constants/CQueryKeys";

import { TFinalizeInvoiceRequest } from "@/types";

import { dateLocaleFor } from "@/i18n/dateLocale";
import { dateFromApi, dateToApi, dueDateFor } from "@/utils";

const NUMBER_PATTERN = /^[A-Za-z0-9._/-]+$/;

interface IInvoiceFinalizeModalProps {
	isOpen: boolean;
	onClose: () => void;
	companyId?: number;
	issuedAt: string;
	dueAt: string;
	paymentTermsDays: number;
	isLoading: boolean;
	onConfirm: (values: TFinalizeInvoiceRequest) => void;
}

/**
 * The last step before an invoice is issued: the number (the next in the
 * user's series unless they type their own, to stay aligned with an
 * external tool) and the two dates. Everything locks after this.
 */
export const InvoiceFinalizeModal: React.FC<IInvoiceFinalizeModalProps> = ({
	isOpen,
	onClose,
	companyId,
	issuedAt: initialIssuedAt,
	dueAt: initialDueAt,
	paymentTermsDays,
	isLoading,
	onConfirm,
}) => {
	const [number, setNumber] = useState("");
	const [issuedAt, setIssuedAt] = useState(initialIssuedAt);
	const [dueAt, setDueAt] = useState(initialDueAt);
	const locale = dateLocaleFor(i18n.language);

	const { data: nextNumber } = useQuery({
		queryKey: invoiceKeys.nextNumber(companyId),
		queryFn: () => InvoicesService.nextNumber(companyId!),
		enabled: isOpen && !!companyId,
	});

	// Fresh values each time it opens: today when the form has no date.
	useEffect(() => {
		if (!isOpen) return;
		const issued = initialIssuedAt || dateToApi(new Date());
		setNumber("");
		setIssuedAt(issued);
		setDueAt(initialDueAt || dueDateFor(issued, paymentTermsDays));
	}, [isOpen, initialIssuedAt, initialDueAt, paymentTermsDays]);

	const typedNumber = number.trim();
	const numberError =
		typedNumber && !NUMBER_PATTERN.test(typedNumber)
			? t("invoices.form.invalidNumber")
			: undefined;

	return (
		<Modal
			open={isOpen}
			onClose={() => {
				if (!isLoading) onClose();
			}}
			size="md"
			title={t("invoices.finalizeModal.title")}
			closeOnOverlayClick={!isLoading}
		>
			<div className="flex flex-col gap-4">
				<InfoMessage color="info" size="sm">
					{t("invoices.finalizeModal.hint")}
				</InfoMessage>
				<Input
					label={t("invoices.form.number")}
					value={number}
					onChange={setNumber}
					placeholder={nextNumber ?? ""}
					error={numberError}
					disabled={isLoading}
				/>
				{nextNumber && !number && (
					<p className="-mt-2 text-xs text-neutral-500">
						{t("invoices.form.numberHint", { next: nextNumber })}
					</p>
				)}
				<div className="grid grid-cols-2 gap-4">
					<DatePicker
						label={t("invoices.form.issuedAt")}
						value={dateFromApi(issuedAt)}
						onChange={(date) => {
							const value = dateToApi(date);
							setIssuedAt(value);
							if (value)
								setDueAt(dueDateFor(value, paymentTermsDays));
						}}
						locale={locale}
						disabled={isLoading}
						portal
					/>
					<DatePicker
						label={t("invoices.form.dueAt")}
						value={dateFromApi(dueAt)}
						onChange={(date) => setDueAt(dateToApi(date))}
						locale={locale}
						disabled={isLoading}
						portal
					/>
				</div>
				<div className="flex justify-end gap-2">
					<Button
						color="white"
						onClick={onClose}
						disabled={isLoading}
					>
						{t("actions.cancel")}
					</Button>
					<Button
						picto="lock"
						isLoading={isLoading}
						disabled={!!numberError || !issuedAt}
						onClick={() =>
							onConfirm({
								...(number.trim()
									? { number: number.trim() }
									: {}),
								issuedAt,
								...(dueAt ? { dueAt } : {}),
							})
						}
					>
						{t("invoices.finalizeModal.confirm")}
					</Button>
				</div>
			</div>
		</Modal>
	);
};
